<template>
  <transition name="fade">
    <div v-if="visible" class="fixed" @click="closePopup">
      <div class="modal-card" @click.stop>
        <div class="modal-header">
          <h2 class="title">{{ props.plan?.nombre }}</h2>
          <button type="button" class="close-btn" @click="closePopup">&times;</button>
        </div>
        <div class="products-section">
          <h4 style="font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-color-light); margin-bottom: 0.75rem;">
            Productos activos
          </h4>
          <span v-if="props.plan.productos.length === 0" class="no-products">No hay productos activos.</span>
          <PlanDetailsProduct 
            v-for="product in props.plan.productos"
            :key="product.id_producto_promo"
            :product="product"
            @update:quantity="(quantity) => updateProductQuantity(product.id_producto_promo, quantity)"
            @add-new-date="(product) => addNewDate(product)" />
        </div>
        <div class="buttons-container">
          <button @click="createNewProduct" class="main_button-green w-full" style="padding: 12px;">Crear nuevo producto</button>
          <button v-if="props.plan.productos.length > 0" @click="updateProductsStock" class="main_button-green w-full" :disabled="!hasChangedData" :class="{'disabled': !hasChangedData}" style="padding: 12px;">Guardar número de plazas</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import PlanDetailsProduct from './PlanDetailsProduct.vue';
import { useLoadingStore } from "@/stores/loadingStore";
import { refreshToken } from "@/services/authService";
import { useRouter } from 'vue-router';
const router = useRouter();

const loadingStore = useLoadingStore();

const props = defineProps({
  plan: {
    type: Object,
  }
});

const emit = defineEmits(['close', 'saved', 'error', 'add-new-date', 'ask-for-confirmation', 'add-new-product']);

const visible = ref(false);
const hasChangedData = ref(false);

const closePopup = () => {
  if (hasChangedData.value === true) {
    emit(
      'ask-for-confirmation',
      '¿Estás seguro de que quieres cerrar el popup sin guardar los datos?',
      () => { 
        visible.value = false;
        emit('close');
      }
    );
    return;
  }

  visible.value = false;
  emit('close');
};

const addNewDate = (product) => {
  if (hasChangedData.value === true) {
    emit(
      'ask-for-confirmation',
      'Para añadir una nueva fecha debes guardar los cambios actuales. ¿Estás seguro de que quieres continuar sin guardar los cambios?',
      () => { 
          hasChangedData.value = false;
          emit('add-new-date', product);
          closePopup();
      }
    );
    return;
  }
  hasChangedData.value = false;
  emit('add-new-date', product);
  closePopup();
};

const updateProductQuantity = (product_id, quantity) => {
  props.plan.productos.forEach(p => {
    if (p.id_producto_promo === product_id) {
      p.stock_maximo = quantity;
      hasChangedData.value = true;
    }
  });
};

watch(
  () => props.plan,
  (newVal) => {
    visible.value = !!newVal
    hasChangedData.value = false;
  }
);

const updateProductsStock = async () => {
  loadingStore.setLoading(true);
  try {
    let response = await updateProductsStockInAPI(props.plan);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await updateProductsStockInAPI(props.plan);
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      emit('error', "Se ha producido un error actualizando el stock.");
    }

    const data = await response.json();

    if (data.errors) {
      loadingStore.setLoading(false);
      emit('error', data.message);
    } else {
      loadingStore.setLoading(false);
      hasChangedData.value = false;
      emit('saved');
      closePopup();
    }
  } catch (exception) {
    loadingStore.setLoading(false);
    emit('error', exception.message);
  }
};

async function updateProductsStockInAPI(plan) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/plans/update_stock", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "plan_id": plan.id_promo,
        "products": plan.productos.map(p => {
          return {
            "id": p.id_producto_promo,
            "stock": p.stock_maximo
          }
        })
      }),
    });
}

const createNewProduct = () => {
  if (hasChangedData.value === true) {
    emit(
      'ask-for-confirmation',
      'Para crear un nuevo producto debes guardar los cambios actuales. ¿Estás seguro de que quieres continuar sin guardar los cambios?',
      () => { 
          hasChangedData.value = false;
          emit('add-new-product', props.plan);
          closePopup();
      }
    );
    return;
  }

  hasChangedData.value = false;
  emit('add-new-product', props.plan);
  closePopup();
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 500;
  padding: 16px;
  box-sizing: border-box;
}

.modal-card {
  background-color: #ffffff;
  padding: 24px;
  border-radius: var(--radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 550px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--very-clear-gray, #eeeeee);
  padding-bottom: 12px;
  flex-shrink: 0;
}

.title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-color-dark, #2f4858);
  margin: 0;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: var(--clear-gray, #607d94);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;
}

.close-btn:hover {
  color: var(--text-color-dark, #2f4858);
  background-color: var(--almost-white, #f5f5f5);
}

.products-section {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-top: 1rem;
}

.no-products {
  color: var(--text-color-light);
  font-size: 14px;
}

.buttons-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-top: 1px solid var(--very-clear-gray, #eeeeee);
  padding-top: 10px ;
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
  